import { Head, Link } from "@inertiajs/react";
import { CheckCircle2, Clock, RotateCcw, ArrowLeft } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import StudentShareDialog from "@/components/shared/StudentShareDialog";
import SchoolShareDialog from "@/components/shared/SchoolShareDialog";
import { Card, CardContent } from "@/components/ui/card";
import { formatMoney, formatDate } from "@/lib/format";

/**
 * The donor's receipt, at /receipts/:token — the thank-you after a
 * payment, and a permalink they can come back to. Only a paid donation
 * is called confirmed. The demo school's pretend donations land here too
 * (/demo/receipt, `demo`).
 */
export default function Receipt({ donation, student, recipient, school, demo = false }) {
  const target = recipient || { name: student?.display_name, public_page_path: student?.public_page_path, school_wide: false };
  const amount = formatMoney(donation.amount_cents, { cents: true });
  const campaignOwner = target.school_wide ? `${school.name}'s read-a-thon` : `${target.first_name || target.name}'s campaign`;
  const hero = heroFor(donation, { amount, campaignOwner, school });
  const HeroIcon = hero.icon;

  return (
    <PublicLayout mainClassName="max-w-lg space-y-8 py-14 md:py-20">
      <Head title={`Donation receipt · ${school.name}`} />

      <div className="space-y-4 text-center">
        <div className={`mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full ${hero.iconClass}`}>
          <HeroIcon className="h-8 w-8" />
        </div>
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">{hero.title}</h1>
        <p className="mx-auto max-w-md text-lg leading-relaxed text-muted-foreground">{hero.body}</p>
      </div>

      {donation.status === "paid" && target.public_page_url && (
        <div className="space-y-2 text-center">
          <p className="text-sm text-muted-foreground">
            {target.school_wide ? "Know someone who'd like to give too?" : `Know someone who'd like to cheer ${target.first_name || target.name} on?`}
          </p>
          {target.school_wide ? (
            <SchoolShareDialog url={target.public_page_url} schoolName={school.name} triggerLabel="Share the school's page" triggerSize="default" />
          ) : (
            <StudentShareDialog url={target.public_page_url} firstName={target.first_name || target.name} schoolName={school.name} triggerLabel={`Share ${target.first_name || target.name}'s page`} triggerVariant="default" triggerSize="default" />
          )}
        </div>
      )}

      <Card>
        <CardContent className="space-y-4 p-6 md:p-6">
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Donation receipt</p>
          <ReceiptRow label="Donation" value={amount} bold />
          {donation.fee_cents > 0 && <ReceiptRow label="Card fee (covered by you)" value={formatMoney(donation.fee_cents, { cents: true })} />}
          {donation.tip_cents > 0 && <ReceiptRow label="Tip to PagePledge" value={formatMoney(donation.tip_cents, { cents: true })} />}
          <div className="border-t border-border pt-3">
            <ReceiptRow label="Total charged" value={formatMoney(donation.total_charged_cents, { cents: true })} bold primary />
          </div>
          {donation.refunded_cents > 0 && <ReceiptRow label="Refunded" value={formatMoney(donation.refunded_cents, { cents: true })} />}

          <div className="space-y-2 border-t border-border pt-4">
            <ReceiptRow label="To" value={target.school_wide ? school.name : `${target.name} · ${school.name}`} />
            {donation.paid_at && <ReceiptRow label="Date" value={formatDate(donation.paid_at)} />}
            <ReceiptRow label="Payment" value={donation.payment_method || "Processed by Square"} />
          </div>

          {donation.donor_message && (
            <div className="border-t border-border pt-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Your message</p>
              <p className="text-sm italic text-foreground">“{donation.donor_message}”</p>
            </div>
          )}

          <div className="rounded-md border border-border bg-background/60 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
            <p>
              <strong className="text-foreground">This isn't a charitable tax receipt.</strong> PagePledge isn't a registered charity and
              doesn't issue official tax receipts.
            </p>
            <p className="mt-1.5">
              {demo ? "This is a demo, so no card was charged and no receipt was emailed." : "We've emailed you a copy of this receipt."}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <Link href={target.public_page_path} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" />
          {target.school_wide ? `Back to ${school.name}` : `Back to ${target.name}'s page`}
        </Link>
      </div>
    </PublicLayout>
  );
}

function heroFor(donation, { amount, campaignOwner, school }) {
  switch (donation.status) {
    case "refunded":
      return { icon: RotateCcw, iconClass: "bg-secondary text-muted-foreground", title: "This donation was refunded", body: `The ${amount} was returned to your card. Questions? Contact ${school.name}.` };
    case "pending":
      return { icon: Clock, iconClass: "bg-info/10 text-info", title: "We're confirming your payment", body: "Your receipt will arrive by email once the bank confirms it." };
    case "failed":
      return { icon: Clock, iconClass: "bg-secondary text-muted-foreground", title: "This payment didn't go through", body: "You weren't charged." };
    default:
      return { icon: CheckCircle2, iconClass: "bg-success/10 text-success", title: `Thank you, ${donation.donor_name}.`, body: `${campaignOwner} just got ${amount} closer to the goal.` };
  }
}

function ReceiptRow({ label, value, bold = false, primary = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-right font-tabular ${bold ? "font-semibold" : ""} ${primary ? "text-lg text-primary" : "text-sm text-foreground"}`}>{value}</span>
    </div>
  );
}
